import { useEffect } from 'react';
import * as Linking from 'expo-linking';
import { supabase } from '../services/supabase';
import { useUserStore } from '../store/userStore';
import { authErrorKey, doitCapturer, resolveAuthError } from '../utils/authErrors';
import { capturerIncident } from '../services/sentry';

// Chemin du lien de confirmation d'email (cf. CONFIRM_REDIRECT dans useAuth). L'OAuth Google passe
// par auth/callback et est capté par openAuthSessionAsync → on ne traite ici QUE la confirmation.
const CONFIRM_PATH = 'auth/confirm';
// Lien de réinitialisation du mot de passe (cf. RESET_REDIRECT dans useAuth). L'échange de code est
// le même ; c'est `onAuthStateChange` (PASSWORD_RECOVERY) qui ouvre l'écran « nouveau mot de passe ».
// Ici ne diffèrent que les conseils en cas d'échec.
const RESET_PATH = 'auth/reset';

/**
 * Intercepte le deep link de confirmation d'email (hormeia://auth/confirm?code=…) qu'aucun listener
 * ne gère aujourd'hui, échange le code contre une session (flow PKCE) et laisse onAuthStateChange
 * (useAuth) basculer l'app en connecté. En cas d'échec, pose une CLÉ i18n affichée par AuthScreen :
 * jamais d'ouverture d'app muette.
 *
 * Les trois causes d'échec sont DISTINCTES et appellent trois conseils différents — elles étaient
 * auparavant écrasées sous un unique « Ce lien a expiré » qui se trompait deux fois sur trois :
 *   1. Supabase refuse la vérification → il pose `error_code` dans l'URL (`otp_expired` quand le
 *      lien a réellement expiré), qui passe par la table de correspondance comme n'importe quel
 *      code serveur.
 *   2. L'échange PKCE échoue → `bad_code_verifier` / `flow_state_not_found`. La cause réelle n'est
 *      pas l'expiration mais l'APPAREIL : le vérifieur vit dans l'AsyncStorage du téléphone qui a
 *      lancé l'inscription. Lien ouvert ailleurs = conseil « ouvre-le sur ce téléphone ».
 *   3. L'appel lève → réseau ou panne, traité par `resolveAuthError`.
 */
export function useAuthDeepLink() {
  const setAuthNoticeKey = useUserStore((state) => state.setAuthNoticeKey);
  const setRecoveryPending = useUserStore((state) => state.setRecoveryPending);

  useEffect(() => {
    const handleUrl = async (url: string | null) => {
      // Le CHEMIN seul : un `auth/reset` glissé dans la query d'un autre lien ne doit pas le faire
      // passer pour une réinitialisation.
      const chemin = url ? url.split(/[?#]/)[0] : '';
      const contexte = chemin.includes(RESET_PATH) ? 'reset' : chemin.includes(CONFIRM_PATH) ? 'confirm' : null;
      if (!url || !contexte) return;

      const { queryParams } = Linking.parse(url);
      if (queryParams?.error || queryParams?.error_description) {
        // `error_code` est le code Supabase ; `error` à côté est du vocabulaire OAuth
        // (`access_denied`) qui ne dit rien de la cause ici. Un code absent tombe en famille 2.
        const code = typeof queryParams.error_code === 'string' ? queryParams.error_code : undefined;
        console.warn('[auth]', contexte, code ?? 'no_code', String(queryParams.error_description ?? queryParams.error));
        const key = authErrorKey(code, contexte);
        // Ce chemin ne passe pas par `resolveAuthError` (le code vient de l'URL, pas d'une
        // AuthError) : la capture doit donc être posée ici aussi — mais avec LA MÊME RÈGLE,
        // `doitCapturer`, et non un test sur la clé obtenue. C'était le dernier endroit où la
        // télémétrie se déduisait de l'affichage ; le laisser vivant aurait rendu invisible dans
        // Sentry tout code de confirmation auquel on donnerait un jour un message dédié.
        if (doitCapturer(code, contexte)) capturerIncident(code ?? 'no_code', contexte);
        setAuthNoticeKey(key);
        return;
      }
      const code = queryParams?.code;
      if (typeof code !== 'string' || !code) return;

      try {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) setAuthNoticeKey(resolveAuthError(error, contexte));
        // Ceinture et bretelles : l'événement PASSWORD_RECOVERY de `useAuth` pose déjà le verrou,
        // mais il peut être émis avant que son écouteur soit monté (démarrage à froid). L'échange a
        // réussi, la session existe : le verrou doit l'être aussi.
        else if (contexte === 'reset') setRecoveryPending(true);
      } catch (error) {
        setAuthNoticeKey(resolveAuthError(error, contexte));
      }
    };

    // Cold start (app ouverte par le lien) + warm (app déjà ouverte).
    Linking.getInitialURL().then(handleUrl).catch(() => undefined);
    const subscription = Linking.addEventListener('url', ({ url }) => handleUrl(url));
    return () => subscription.remove();
  }, [setAuthNoticeKey, setRecoveryPending]);
}
